import React from "react";
import { FiCornerUpLeft, FiHeart } from "react-icons/fi";
import { CommentType } from "../types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

interface CommentCardProps {
  comment: CommentType;
  isBackground?: boolean;
  depth?: number;
  onReply?: (commentId: number, content: string) => Promise<void>;
}

const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  isBackground = false,
  depth = 0,
  onReply,
}) => {
  const handleReply = () => {
    const content = prompt("Enter your reply:");
    if (content && onReply) {
      onReply(comment.id, content);
    }
  };

  const depthColors = [
    "var(--color-forest)",
    "var(--color-terracotta)",
    "#6B8E6B",
    "#8B7355",
  ];

  const borderColor = depthColors[Math.min(depth, depthColors.length - 1)];
  const isRemoved = comment.isRemoved === true;

  return (
    <div
      style={{
        backgroundColor: isBackground ? "#f8f8f5" : "white",
        borderRadius: 16,
        padding: 16,
        boxShadow: isBackground
          ? "0 2px 8px rgba(0,0,0,0.05)"
          : "0 10px 24px rgba(24,34,53,0.09)",
        border: `1px solid ${isBackground ? "var(--color-border)" : borderColor}`,
        opacity: isBackground ? 0.8 : 1,
        pointerEvents: isBackground ? "none" : "auto",
        fontFamily: "var(--font-primary)",
        minHeight: depth === 0 ? 180 : 120,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 12,
          gap: 10,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: borderColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {comment.user?.name?.charAt(0).toUpperCase() || "?"}
        </div>

        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              color: "var(--color-charcoal)",
              fontSize: 14,
            }}
          >
            {comment.user?.name || "Anonymous"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-gray-warm)",
            }}
          >
            {dayjs(comment.createdAt).fromNow()}
          </div>
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="gist-comment-replies-badge">
            {comment.replies.length} {comment.replies.length === 1 ? "reply" : "replies"}
          </div>
        )}
      </div>

      <div
        style={{
          flex: 1,
          color: "var(--color-charcoal)",
          fontSize: 14,
          lineHeight: 1.55,
          overflow: "hidden",
        }}
      >
        {isRemoved ? (
          <span className="comment-removed-placeholder">{comment.comment}</span>
        ) : (
          comment.comment
        )}
      </div>

      {!isBackground && !isRemoved && (
        <div className="gist-comment-card-actions">
          <button onClick={handleReply} className="gist-comment-card-link" type="button">
            <FiCornerUpLeft />
            <span>Reply</span>
          </button>
          <button className="gist-comment-card-link is-muted" type="button">
            <FiHeart />
            <span>Like</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CommentCard;
